'use client';

import React, { useState } from 'react';

interface Payment {
  id: number;
  property: string;
  date: string;
  amount: string;
  method: string;
  status: 'paid' | 'pending' | 'refunded';
  bookingRef: string;
}

const payments: Payment[] = [
  { id: 1, property: 'Luxury Villa Kololo', date: 'Aug 20, 2026', amount: 'UGX 2,500,000', method: 'Mobile Money', status: 'paid', bookingRef: 'NDT-2026-0821' },
  { id: 2, property: 'Lakeside Retreat Entebbe', date: 'Aug 18, 2026', amount: 'UGX 1,200,000', method: 'Visa Card', status: 'paid', bookingRef: 'NDT-2026-0834' },
  { id: 3, property: 'Safari Lodge Murchison', date: 'Jul 5, 2026', amount: 'UGX 3,200,000', method: 'Bank Transfer', status: 'paid', bookingRef: 'NDT-2026-0712' },
  { id: 4, property: 'Mountain Cabin Rwenzori', date: 'May 15, 2026', amount: 'UGX 900,000', method: 'Mobile Money', status: 'paid', bookingRef: 'NDT-2026-0520' },
  { id: 5, property: 'City Apartment Kampala', date: 'Apr 2, 2026', amount: 'UGX 450,000', method: 'Visa Card', status: 'refunded', bookingRef: 'NDT-2026-0402' },
];

const statusConfig: Record<string, { label: string; color: string }> = {
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  refunded: { label: 'Refunded', color: 'bg-blue-100 text-blue-700' },
};

const methodIcons: Record<string, string> = {
  'Mobile Money': '📱',
  'Visa Card': '💳',
  'Bank Transfer': '🏦',
};

export default function GuestPayments() {
  const [activeFilter, setActiveFilter] = useState<'all' | 'paid' | 'refunded'>('all');

  const filtered = payments.filter((p) => activeFilter === 'all' || p.status === activeFilter);
  const totalSpent = payments.filter((p) => p.status === 'paid').length;

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Total Spent (2026)</p>
          <p className="text-2xl font-bold text-gray-900">UGX 8,250,000</p>
          <p className="text-xs text-green-600 mt-1">↑ 12% vs last year</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Bookings Paid</p>
          <p className="text-2xl font-bold text-gray-900">{totalSpent}</p>
          <p className="text-xs text-gray-400 mt-1">Across 3 countries</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Refunds Received</p>
          <p className="text-2xl font-bold text-gray-900">UGX 450,000</p>
          <p className="text-xs text-blue-600 mt-1">1 refund processed</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-5">
        {(['all', 'paid', 'refunded'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all ${
              activeFilter === f ? 'bg-rose-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-rose-300'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Property</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Method</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ref</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((payment) => (
                <tr key={payment.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 text-sm font-semibold text-gray-900">{payment.property}</td>
                  <td className="px-5 py-4 text-sm text-gray-500">{payment.date}</td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1.5">
                      <span>{methodIcons[payment.method]}</span>
                      {payment.method}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm font-bold text-gray-900">{payment.amount}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusConfig[payment.status].color}`}>
                      {statusConfig[payment.status].label}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs font-mono text-gray-500">{payment.bookingRef}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
